import { useSnapshot } from 'valtio'
import cn from 'classnames'
import { type ReactElement, useCallback } from 'react'
import { recurringPaymentsProxy } from '../../../recurringPaymentsProxy'
import {
  executeRetrieveRecurringPaymentsSearch
} from '../../../../../../../shared/apiUtils/executeRetrieveRecurringPaymentsSearch'
import { RecurringPaymentStatus } from '../../../../../../../shared/types/GlobalRecurringPaymentsStatus'
import { proxyGlobalAuth } from '../../../../../../../shared/proxies/proxyGlobalAuth'
import { Toggle } from '../../../../../../../shared/components/ui/toggles/Toggle'
import { StartDateFilter } from '../components/Dates/StartDateFilter'
import { EndDateFilter } from '../components/Dates/EndDateFilter'

interface ExpandedFiltersByProps {
  closeExpanded: () => void
}

export function ExpandedFiltersBy ({ closeExpanded }: ExpandedFiltersByProps): ReactElement {
  const { filters } = useSnapshot(recurringPaymentsProxy)
  const onClear = useCallback(() => {
    recurringPaymentsProxy.filters.statuses = []
    recurringPaymentsProxy.filters.startDate = null
    recurringPaymentsProxy.filters.endDate = null
    closeExpanded()
    void executeRetrieveRecurringPaymentsSearch({ displayLoader: false })
  }, [closeExpanded])

  const onApply = useCallback(() => {
    closeExpanded()
    void executeRetrieveRecurringPaymentsSearch({ displayLoader: false })
  }, [closeExpanded])

  const onStatusToggle = (status: RecurringPaymentStatus) => () => {
    if (recurringPaymentsProxy.filters.statuses.includes(status)) {
      recurringPaymentsProxy.filters.statuses = recurringPaymentsProxy.filters
        .statuses
        .filter(t => t !== status)
    } else {
      recurringPaymentsProxy.filters.statuses = [
        ...recurringPaymentsProxy.filters.statuses,
        status
      ]
    }
  }
  const { siteTheme: { datepickerFilterColors } } = useSnapshot(proxyGlobalAuth)
  return (
    <div
      className="absolute top-12 right-0 rounded border border-gray-200 animate__animated animate__fadeIn bg-white z-10"
      style={{ animationDuration: '100ms' }}
    >
      <div className="flex flex-row">
        <div className="px-6 pt-2 pb-3 bg-white">
          <h5 className="font-bold mt-3 mb-4">Type</h5>
          <div className="w-48 flex justify-between my-6">
            <div className="text-sm ">Active</div>
            <Toggle
              name="active"
              onClick={onStatusToggle(RecurringPaymentStatus.Active)}
              selected={filters.statuses.includes(RecurringPaymentStatus.Active)}
            />
          </div>

          <div className="flex justify-between my-6">
            <div className="text-sm">Ended</div>
            <Toggle
              name="ended"
              onClick={onStatusToggle(RecurringPaymentStatus.Ended)}
              selected={filters.statuses.includes(RecurringPaymentStatus.Ended)}
            />
          </div>

          <div className="flex justify-between my-6">
            <div className="text-sm">Paused</div>
            <Toggle
              name="paused"
              onClick={onStatusToggle(RecurringPaymentStatus.Paused)}
              selected={filters.statuses.includes(RecurringPaymentStatus.Paused)}
            />
          </div>

          <div className="flex justify-between my-6">
            <div className="text-sm">Unsuccessful</div>
            <Toggle
              name="unsuccessful"
              onClick={onStatusToggle(RecurringPaymentStatus.Errored)}
              selected={filters.statuses.includes(RecurringPaymentStatus.Errored)}
            />
          </div>

        </div>
        <div>
          <div className="px-6 pt-2 pb-3 bg-white">
            <h5 className="font-bold mt-3 mb-4">Date Range</h5>
            <StartDateFilter />
            <EndDateFilter />
          </div>
        </div>
      </div>

      <div className={cn(datepickerFilterColors.buttonBackgroundClasses, ' h-12 w-full flex justify-between py-2 px-12')}>
        <button
          className={cn(datepickerFilterColors.clearButtonClasses, 'text-white')}
          onClick={onClear}
        >
          Clear
        </button>
        <button
          className={cn(datepickerFilterColors.applyButtonClasses, 'font-semibold bg-white rounded px-5')}
          onClick={onApply}
        >
          Apply
        </button>
      </div>
    </div>
  )
}
